'use client';

export interface QueryCacheConfig {
  cacheName: string;
  /** Delay (ms) before starting background revalidation after a cache hit. */
  revalidateDelay?: number;
}

/** Convert a cache key to a cache-friendly URL. */
function cacheUrl(key: string): string {
  return key.startsWith('http') ? key : `https://cache${key}`;
}

/** Reads a JSON value from the Cache API by key, returning undefined on cache miss or error. */
export async function readCache(cacheName: string, key: string): Promise<unknown | undefined> {
  try {
    const cache = await caches.open(cacheName);
    const response = await cache.match(cacheUrl(key));
    if (!response) return undefined;
    return await response.json();
  } catch {
    return undefined;
  }
}

/** Writes a JSON value to the Cache API by key; failures are silently swallowed since cache writes are non-fatal. */
export async function writeCache(cacheName: string, key: string, data: unknown): Promise<void> {
  try {
    const cache = await caches.open(cacheName);
    await cache.put(
      cacheUrl(key),
      new Response(JSON.stringify(data), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
  } catch {
    // Cache write failure is non-fatal
  }
}
